import { dateTimeFormat } from '@/utility/utility_functions'
import ProfileType from '@/classes/auth/ProfileType'
import FormDescriptor from '@/classes/form/FormDescriptor'
import FormData from '@/classes/form/FormData'
import Profile from '@/classes/auth/Profile'
import State from '@/classes/case/State'
import File from '@/classes/case/File'
import FormField from '../form/FormField'
import User from '@/classes/auth/User'

class ReviewResult {
  static FILE_TYPE = 'FILE'
  static MULTIPLE_FILES_TYPE = 'MULTIPLE-FILES'
  static RADIO_CHECKBOX_TYPE = ['RADIO', 'MULTIPLE-CHECKBOX']
  static CHECKBOX_TYPE = 'CHECKBOX'
  static OTHERS_TYPE = ['TEXT']

  constructor (id, state, formDescriptor, formData, finding, _case, responsible) {
    this.id = id
    this.state = state
    this.formDescriptor = formDescriptor
    this.formData = formData
    this.finding = finding
    this.pendingFinding = null
    this.case = _case
    this.responsible = responsible
    this.comments = []
    this.triggerToggle = false
    this.dependencies = this.createDependencies()
  }

  get value () {
    let fieldId = this.formDescriptor.fieldSets[0].fields[0].id
    let field = this.formData.fields[fieldId]
    if (FormField.isFileField(field.type)) {
      return File.asFiles(field.files || [])
    }
    return field.value
  }

  // according to the current pattern the formDescriptors for ReviewResult
  // always contains a single fieldSet with a single field (even though it is an array)
  get fieldData () {
    return this.formData.fields[this.formDescriptor.fieldSets[0].fields[0].id]
  }

  static asReviewResults (json) {
    return json.map(jsonCase => {
      return ReviewResult.asReviewResult(jsonCase)
    })
  }

  static asReviewResult (json) {
    let formDescriptors = FormDescriptor.asFormDescriptors([{
      id: 1,
      field_sets: json.field_sets
    }])

    let formDatas = FormData.initiateFormDatas(formDescriptors)
    FormData.loadInitialFormDatas([json.form_data], formDatas)
    let finding = null
    if (json.findings && json.findings.length > 0) {
      finding = json.findings[0]
    }

    return new ReviewResult(
      json.id,
      ReviewResultState.asState(json.state),
      formDescriptors[0],
      formDatas[0],
      finding ? ReviewResultFinding.asReviewResultFinding(finding) : null,
      json.case,
      json.responsible ? ProfileType.asProfileType(json.responsible) : null
    )
  }

  static updateListFormResults (currentResults, updatedResults) {
    currentResults.forEach(currentResult => {
      let updated = updatedResults.find(updatedResult => {
        return updatedResult.id === currentResult.id
      })
      if (updated) {
        currentResult.updateFromResult(updated)
      }
    })
  }

  createDependencies () {
    let dependencies = []
    for (let fieldSet of this.formDescriptor.fieldSets) {
      if (fieldSet.dependencies.length) {
        for (let field of fieldSet.fields) {
          let data = this.formData.fields[field.id]
          let dep = new ReviewResultDependency(
            field.id,
            field.uid,
            field.label,
            field.type,
            data && data.value,
            field.type === 'FILE' ? data.files : []
          )
          dependencies.push(dep)
        }
      }
    }
    return dependencies
  }

  updateFromResult (result) {
    if (this.id !== result.id) {
      throw new Error('Objects missmatch')
    }

    this.state = result.state
    this.formDescriptor = result.formDescriptor
    this.formData = result.formData
    this.finding = result.finding
    this.case = result.case
    this.responsible = result.responsible
  }

  setResponsible (json) {
    this.responsible = new ProfileType(json.profile_type_id, json.profile_type_display)
  }

  resetComments () {
    this.comments = []
  }

  setComments (comments) {
    this.comments = comments
  }

  addComment (comment) {
    this.comments.push(comment)
  }

  addOrSetPendingFinding (json) {
    if (!isEmpty(this.finding)) {
      // We are adding a new comment if the finding already exists
      this.finding.addPendingComment(json.user)
      return
    }
    this.pendingFinding = new ReviewResultFinding(null, '', json.user, json.user.profile, [])
  }

  commitPendingFinding () {
    this.finding = this.pendingFinding
    this.pendingFinding = null
  }

  getLabel () {
    return this.formDescriptor.fieldSets[0].fields[0].label
  }

  getValue () {
    return this.fieldData ? this.fieldData.displayValue : ''
  }

  isEmptyFormData () {
    return !this.formData.length
  }

  isFileField () {
    return FormField.isFileField(this.fieldData.type)
  }

  getDependencyValue () {
    let value = ''
    for (let prop in this.formData.fields) {
      if (!this.formData.dependencies[prop] && this.formData.fields[prop].value) {
        value += this.formData.fields[prop].displayValue + ' '
      }
    }
    return value || ''
  }

  getDataGroup () {
    return this.formDescriptor.fieldSets[0].fields[0].dataGroup
  }

  getDataType () {
    return this.formDescriptor.fieldSets[0].fields[0].type
  }

  setState (state) {
    this.state = ReviewResultState.getStateByValue(state)
  }

  isPending () {
    return this.state.value === ReviewResultState.PENDING
  }

  isOK () {
    return this.state.value === ReviewResultState.OK
  }

  isNotOK () {
    return this.state.value === ReviewResultState.NOT_OK
  }

  hasFinding () {
    return !isEmpty(this.finding)
  }

  waitingOnCustomer () {
    return this.responsible.isCustomer() && this.hasFinding()
  }

  waitingOnAuditor () {
    return this.responsible && this.responsible.isAuditor()
  }

  startingNewReview () {
    return (this.responsible.isAuditor() || this.responsible.isAdministrator()) && !this.hasFinding()
  }

  waitingOnReview () {
    return (this.responsible.isAuditor() || this.responsible.isAdministrator()) && this.hasFinding()
  }

  waitingOnReviewer (reviewer) {
    return this.responsible && this.responsible.profileType === reviewer.profileType
  }

  getStateClass () {
    if (this.waitingOnCustomer()) {
      return State.STATE_TAG_CLASS[1]
    }
    if (this.waitingOnAuditor()) {
      return State.STATE_TAG_CLASS[1]
    }
    return State.STATE_TAG_CLASS[0]
  }

  getStateLevel () {
    if (this.waitingOnCustomer()) {
      return State.INFO
    }
    if (this.waitingOnAuditor()) {
      return State.INFO
    }
    return State.DEFAULT
  }

  getStateDisplay () {
    if (this.waitingOnCustomer()) {
      return 'Waiting on customer'
    }
    if (this.waitingOnAuditor()) {
      return 'Waiting on auditor'
    }
    return 'Undefined'
  }

  isRejected () {
    let lastComment = this.finding.comments[this.finding.comments.length - 1]
    return lastComment &&
      lastComment.owner.isAuditor() &&
      this.responsible.isCustomer()
  }
}

class ReviewResultState {
  static PENDING = 1
  static OK = 2
  static NOT_OK = 3

  static STATES = {
    [ReviewResultState.PENDING]: {
      value: ReviewResultState.PENDING,
      display: 'Pending',
      level: State.INFO
    },
    [ReviewResultState.OK]: {
      value: ReviewResultState.OK,
      display: 'OK',
      level: State.INFO
    },
    [ReviewResultState.NOT_OK]: {
      value: ReviewResultState.NOT_OK,
      display: 'Not OK',
      level: State.DANGER
    }
  }

  constructor (value, display, level) {
    this.value = value
    this.display = display
    this.level = level
  }

  static asStates (json) {
    return json.map(state => {
      return ReviewResultState.asState(state)
    })
  }

  static asState (json) {
    return new ReviewResultState(
      json.value,
      json.display,
      json.level
    )
  }

  static getStateClass (level) {
    return State.STATE_TAG_CLASS[level]
  }

  static getStateByValue (value) {
    return ReviewResultState.STATES[value]
  }
}

class ReviewResultFinding {
  constructor (id, text, user, profile, comments, created, modified) {
    this.id = id
    this.text = text
    this.user = user
    this.profile = profile
    this.comments = comments
    this.pendingComment = null
    this.created = created
    this.modified = modified
  }

  static asReviewResultFindings (json) {
    return json.map(jsonCase => {
      return ReviewResultFinding.asReviewResultFinding(jsonCase)
    })
  }

  static asReviewResultFinding (json) {
    return new ReviewResultFinding(
      json.id ? json.id : null,
      json.text ? json.text : '',
      json.user ? User.asUser(json.user) : {},
      json.profile ? Profile.asProfile(json.profile) : {},
      json.comments ? Comment.asComments(json.comments) : [],
      json.created ? dateTimeFormat(json.created) : '',
      json.modified ? dateTimeFormat(json.modified, 'YYYY-MM-DD HH:mm') : ''
    )
  }

  isSwitched () {
    return (this.profile.id && this.user.id && this.user.profile.id)
      ? (this.profile.id !== this.user.profile.id &&
      this.profile.profileType !== this.user.profile.profileType) : null
  }

  hasComments () {
    return this.comments && this.comments.length > 0
  }

  hasPendingComment () {
    return this.pendingComment !== null
  }

  addPendingComment (user) {
    this.pendingComment = new Comment('', '', user, user.profile, dateTimeFormat(new Date()))
  }

  commitPendingComment () {
    this.comments.push(this.pendingComment)
    this.pendingComment = null
  }

  hasAnsweredFinding () {
    if (this.comments.length > 0) {
      let comment = this.comments[this.comments.length - 1]
      return comment.profile.isCustomer()
    }
    return false
  }
}

class Comment {
  constructor (id, text, user, profile, created) {
    this.id = id
    this.text = text
    this.user = user
    this.profile = profile
    this.created = created
    this.isValid = true
  }

  isSwitched () {
    return (this.profile.id && this.user.id && this.user.profile.id)
      ? (this.profile.id !== this.user.profile.id &&
      this.profile.profileType !== this.user.profile.profileType) : null
  }

  static asComments (json) {
    return json.map(jsonCase => {
      return Comment.asComment(jsonCase)
    })
  }

  static asComment (json) {
    return new Comment(
      json.id,
      json.text,
      json.user ? User.asUser(json.user) : {},
      json.profile ? Profile.asProfile(json.profile) : {},
      json.created ? dateTimeFormat(json.created) : dateTimeFormat(new Date())
    )
  }

  isEmpty () {
    return this.text.length === 0
  }
}

class ReviewResultDependency {
  constructor (id, uid, label, type, value, files) {
    this.id = id
    this.uid = uid
    this.label = label
    this.type = type
    this.value = value
    this.files = files
  }
}

function isEmpty (obj) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      return false
    }
  }
  return true
}

export {
  ReviewResult,
  ReviewResultState,
  ReviewResultFinding,
  Comment
}
